import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MessageComposeFields } from '@/features/messaging/MessageComposeFields';
import {
  EMPTY_DRAFT_FIELDS,
  draftFieldsFromLists,
  splitRecipients,
  useMessageDraftLifecycle,
  type DraftFields,
} from '@/features/messaging/useMessageDraftLifecycle';
import { describeAccount, readComposeDraftFields } from '@/features/messaging/message-compose-shared';
import { useUser } from '@/hooks';
import { useMemberExternalAccounts } from '@/hooks/useMemberExternalAccountQueries';
import { composerContainerClassName } from '@/lib/composer-position';
import { composeMessageDraft } from '@/api/content-api';
import { useComposerStore } from '@/store/useComposerStore';
import { useSpeak2MeStore } from '@/store/speak2meStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { ChevronDown, Minus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function MessageComposer() {
  const isOpen = useComposerStore((s) => s.isOpen);
  const isMinimized = useComposerStore((s) => s.isMinimized);
  const fields = useComposerStore((s) => s.fields);
  const draftContentId = useComposerStore((s) => s.draftContentId);
  const dirty = useComposerStore((s) => s.dirty);
  const setFields = useComposerStore((s) => s.setFields);
  const replaceFields = useComposerStore((s) => s.replaceFields);
  const bindDraft = useComposerStore((s) => s.bindDraft);
  const setDirty = useComposerStore((s) => s.setDirty);
  const closeCompose = useComposerStore((s) => s.closeCompose);
  const minimize = useComposerStore((s) => s.minimize);
  const restore = useComposerStore((s) => s.restore);

  const neoOpen = useSpeak2MeStore((s) => s.isOpen);
  const navigate = useNavigate();
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember.id ?? 0;
  const accountsQuery = useMemberExternalAccounts(memberId);

  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const selectable = useMemo(
    () => (accountsQuery.data?.items ?? []).filter((a) => a.outboundCapable && a.isActive),
    [accountsQuery.data?.items]
  );
  const selectableKey = selectable.map((a) => a.id).join(',');

  useEffect(() => {
    if (isOpen && fields.accountId == null && selectable.length > 0) {
      const def = selectable.find((a) => a.isDefaultOutbound) ?? selectable[0];
      replaceFields({ accountId: def.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fields.accountId, selectableKey]);

  useEffect(() => {
    if (!isOpen) return;
    useViewContextStore.getState().setSlot('messageComposer', {
      kind: 'compose',
      ...(draftContentId !== null ? { draftContentId } : {}),
      unsaved: {
        to: fields.to,
        cc: fields.cc,
        bcc: fields.bcc,
        subject: fields.subject,
        body: fields.authoredBody,
      },
    });
    return () => useViewContextStore.getState().clearSlot('messageComposer');
  }, [isOpen, draftContentId, fields.to, fields.cc, fields.bcc, fields.subject, fields.authoredBody]);

  const accountId = fields.accountId;
  const create = useCallback(
    async (draft: DraftFields): Promise<{ draftContentId: number; fields: DraftFields }> => {
      const result = await composeMessageDraft({
        ...(accountId !== null ? { memberExternalAccountId: accountId } : {}),
        to: splitRecipients(draft.to),
        cc: splitRecipients(draft.cc),
        bcc: splitRecipients(draft.bcc),
        subject: draft.subject,
        body: draft.authoredBody,
      });
      return { draftContentId: result.draftContentId, fields: draftFieldsFromLists(result.resolved) };
    },
    [accountId]
  );

  const lifecycle = useMessageDraftLifecycle({
    flushKey: 'message-composer',
    active: isOpen,
    fields,
    baseline: EMPTY_DRAFT_FIELDS,
    draftContentId,
    dirty,
    onBound: bindDraft,
    onDirtyChange: setDirty,
    onReplaceFields: replaceFields,
    create,
    readRemoteFields: readComposeDraftFields,
  });

  if (!isOpen) {
    return null;
  }

  const hasAccounts = selectable.length > 0;
  const selectedAccount = selectable.find((a) => a.id === fields.accountId) ?? null;
  const busy = isSending || isClosing;
  const toRecipients = splitRecipients(fields.to);
  const canSend = hasAccounts && fields.accountId != null && toRecipients.length > 0 && !busy;

  const patch = (next: Partial<DraftFields>): void => {
    if (error) {
      setError(null);
    }
    setFields(next);
    lifecycle.noteEdit();
  };

  const handleSend = async (): Promise<void> => {
    if (!canSend) {
      return;
    }
    setError(null);
    setIsSending(true);
    try {
      await lifecycle.send();
      closeCompose();
      toast.success('Message sent');
    } catch (sendError) {
      const message = getAxiosErrorDisplayMessage(sendError, 'Failed to send message.');
      setError(message);
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = async (): Promise<void> => {
    setIsClosing(true);
    try {
      const outcome = await lifecycle.close();
      if (outcome === 'kept') {
        toast.success('Draft kept');
      }
      closeCompose();
    } catch (closeError) {
      const message = getAxiosErrorDisplayMessage(closeError, 'Failed to close the composer.');
      setError(message);
      toast.error(message);
    } finally {
      setIsClosing(false);
    }
  };

  const containerClassName = composerContainerClassName(neoOpen);

  const renderAccountSelector = () => {
    if (!hasAccounts) {
      return null;
    }
    if (selectable.length === 1) {
      return (
        <span className="truncate text-xs text-muted-foreground" title={describeAccount(selectable[0])}>
          {describeAccount(selectable[0])}
        </span>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={busy || draftContentId !== null}
            className="inline-flex max-w-[16rem] items-center gap-1 truncate rounded-full bg-muted/60 px-3 py-1 text-xs hover:bg-muted disabled:opacity-60"
          >
            <span className="truncate">
              {selectedAccount ? describeAccount(selectedAccount) : 'Choose account'}
            </span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {selectable.map((account) => (
            <DropdownMenuItem key={account.id} onSelect={() => replaceFields({ accountId: account.id })}>
              {describeAccount(account)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <section
      role="region"
      aria-label="New message composer"
      className={containerClassName}
    >
      <div className="flex items-center justify-between gap-2 rounded-t-lg border-b bg-muted/40 px-3 py-2">
        <button
          type="button"
          onClick={isMinimized ? restore : minimize}
          className="min-w-0 flex-1 text-left text-sm font-medium"
        >
          New message
        </button>
        <div className="flex items-center gap-1">
          {!isMinimized ? renderAccountSelector() : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={isMinimized ? 'Restore composer' : 'Minimize composer'}
            onClick={isMinimized ? restore : minimize}
          >
            {isMinimized ? <ChevronDown className="h-4 w-4 rotate-180" /> : <Minus className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Close composer"
            disabled={busy}
            onClick={() => void handleClose()}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isMinimized ? null : !hasAccounts ? (
        <div className="space-y-3 p-4">
          <p className="text-sm text-muted-foreground">Connect an email account to send messages.</p>
          <Button type="button" onClick={() => navigate('/settings/integrations')}>
            Connect account
          </Button>
        </div>
      ) : (
        <MessageComposeFields
          fields={fields}
          disabled={busy}
          onChange={patch}
          onBlur={lifecycle.saveNow}
          onSend={() => void handleSend()}
          canSend={canSend}
          isSending={isSending}
          error={error}
          revision={lifecycle.revision}
        />
      )}
    </section>
  );
}
